import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Typography, Card, CardContent, Button, IconButton, Tooltip, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from "@mui/material";
import ModernToggle from "../ModernToggle";
import { BarChart as MuiBarChart } from "@mui/x-charts";
import { ExpandMore, Info } from "@mui/icons-material";

// Filter Section Component - Individual question view with Chart/Table toggle (formerly Stats Section)
const FiltersSection = ({ statsData, topicsData, likertData }) => {
    const intl = useIntl();
    const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
    const [isTableView, setIsTableView] = useState(true);
    const [attrDetailModalOpen, setAttrDetailModalOpen] = useState(false);
    const [selectedAttrDetail, setSelectedAttrDetail] = useState(null);

    // Reset selectedQuestionIndex if it's out of bounds
    useEffect(() => {
        if (statsData && statsData.questions) {
            const { questions } = statsData;
            if (!questions || questions.length === 0) {
                setSelectedQuestionIndex(0);
            } else if (selectedQuestionIndex >= questions.length) {
                setSelectedQuestionIndex(0);
            }
        }
    }, [statsData, selectedQuestionIndex]);

    if (!statsData) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}><FormattedMessage id="filtersSection.demographics_filter" defaultMessage="Demographics Filter" /></Typography>
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="filtersSection.no_demographics_data" defaultMessage="No demographics data available" />
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const { questions } = statsData;

    if (!questions || questions.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}><FormattedMessage id="filtersSection.demographics_filter" defaultMessage="Demographics Filter" /></Typography>
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="filtersSection.no_question_data" defaultMessage="No question data available" />
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Prepare bar chart data for selected question
    const selectedQuestion = questions[selectedQuestionIndex];
    const barChartData = selectedQuestion && selectedQuestion.choices ?
        selectedQuestion.choices.map(choice => ({
            text: choice.text,
            count: choice.responses_count || 0
        })) : [];

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h6">
                        <FormattedMessage id="filtersSection.demographics_filter" defaultMessage="Demographics Filter" />
                    </Typography>
                    <ModernToggle
                        leftLabel={intl.formatMessage({ id: "filtersSection.bar_chart", defaultMessage: "Bar Chart" })}
                        rightLabel={intl.formatMessage({ id: "filtersSection.table", defaultMessage: "Table" })}
                        checked={isTableView}
                        onChange={setIsTableView}
                        size="medium"
                    />
                </Box>

                {/* Question Selector - Horizontal Buttons */}
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 3,
                    justifyContent: 'center'
                }}>
                    {questions.map((question, index) => (
                        <Button
                            key={index}
                            variant={selectedQuestionIndex === index ? "contained" : "outlined"}
                            onClick={() => setSelectedQuestionIndex(index)}
                            sx={{
                                backgroundColor: selectedQuestionIndex === index ? '#1a365d' : 'transparent',
                                borderColor: '#1a365d',
                                color: selectedQuestionIndex === index ? '#ffffff' : '#1a365d',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 2,
                                py: 0.75,
                                borderRadius: 2,
                                minWidth: 'auto',
                                '&:hover': {
                                    backgroundColor: selectedQuestionIndex === index ? '#0f2440' : 'rgba(26, 54, 93, 0.08)',
                                    borderColor: '#1a365d',
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {question.title.length > 60 ? question.title.substring(0, 60) + "..." : question.title}
                        </Button>
                    ))}
                </Box>

                {isTableView ? (
                    /* Table View */
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TableContainer component={Paper} sx={{ maxHeight: 400, maxWidth: 700, width: '100%' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#3D6BDF' }}>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#ffffff', backgroundColor: '#3D6BDF' }}>
                                            <FormattedMessage id="filtersSection.choice" defaultMessage="Choice" />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffffff', backgroundColor: '#3D6BDF' }}>
                                            <FormattedMessage id="filtersSection.count" defaultMessage="Count" />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffffff', backgroundColor: '#3D6BDF' }}>
                                            <FormattedMessage id="filtersSection.percentage" defaultMessage="Percentage" />
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff', backgroundColor: '#3D6BDF', width: 50 }}>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {barChartData.map((row, index) => {
                                        const totalResponses = barChartData.reduce((sum, item) => sum + item.count, 0);
                                        const percentage = totalResponses > 0 ? ((row.count / totalResponses) * 100).toFixed(1) : '0.0';

                                        return (
                                            <TableRow key={index} hover>
                                                <TableCell sx={{ fontWeight: 'medium' }}>
                                                    {row.text}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.count}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Typography variant="body2">
                                                            {percentage}%
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                width: 60,
                                                                height: 8,
                                                                backgroundColor: '#e0e0e0',
                                                                borderRadius: 4,
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: `${percentage}%`,
                                                                    height: '100%',
                                                                    backgroundColor: '#1976d2',
                                                                    transition: 'width 0.3s ease'
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: 50 }}>
                                                    <Tooltip title={intl.formatMessage({ id: "filtersSection.view_distribution", defaultMessage: "View distribution" })}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedAttrDetail(row.text);
                                                                setAttrDetailModalOpen(true);
                                                            }}
                                                            sx={{ color: '#1976d2' }}
                                                        >
                                                            <Info fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                ) : (
                    /* Bar Chart View */
                    <Box sx={{ height: 400, display: "flex", alignItems: "center", position: "relative" }}>
                        <MuiBarChart
                            dataset={barChartData.map(item => {
                                const totalResponses = barChartData.reduce((sum, row) => sum + row.count, 0);
                                const percentage = totalResponses > 0 ? ((item.count / totalResponses) * 100).toFixed(1) : '0.0';
                                return {
                                    ...item,
                                    percentage: percentage
                                };
                            })}
                            xAxis={[{
                                scaleType: 'band',
                                dataKey: 'text',
                                labelStyle: {
                                    fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                },
                                tickLabelStyle: {
                                    fontSize: 11,
                                    fill: '#666',
                                    fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                },
                                valueFormatter: (value) => {
                                    // Truncate long labels to max 18 characters per line, max 2 lines
                                    if (value.length <= 18) return value;
                                    const words = value.split(' ');
                                    let line1 = '';
                                    let line2 = '';
                                    let onLine2 = false;

                                    for (const word of words) {
                                        if (!onLine2 && (line1 + ' ' + word).trim().length <= 18) {
                                            line1 = (line1 + ' ' + word).trim();
                                        } else if (!onLine2) {
                                            onLine2 = true;
                                            line2 = word;
                                        } else if ((line2 + ' ' + word).trim().length <= 15) {
                                            line2 = (line2 + ' ' + word).trim();
                                        } else {
                                            line2 = line2.substring(0, 12) + '...';
                                            break;
                                        }
                                    }

                                    return line2 ? `${line1}\n${line2}` : line1;
                                }
                            }]}
                            yAxis={[{
                                label: intl.formatMessage({ id: "filtersSection.response_count", defaultMessage: "Response Count" }),
                                labelOffset: 20,
                                labelStyle: {
                                    fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                },
                                tickLabelStyle: {
                                    fontSize: 12,
                                    fill: '#666',
                                    fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                                }
                            }]}
                            series={[{
                                dataKey: 'count',
                                label: intl.formatMessage({ id: "filtersSection.response_count", defaultMessage: "Response Count" }),
                                color: '#1976d2',
                                valueFormatter: (value, context) => {
                                    if (context.dataIndex !== undefined) {
                                        const item = barChartData[context.dataIndex];
                                        const totalResponses = barChartData.reduce((sum, row) => sum + row.count, 0);
                                        const percentage = totalResponses > 0 ? ((item.count / totalResponses) * 100).toFixed(1) : '0.0';
                                        return intl.formatMessage(
                                            { id: "filtersSection.responses_tooltip", defaultMessage: "{text}: {count} responses ({percentage}%)" },
                                            { text: item.text, count: value, percentage }
                                        );
                                    }
                                    return intl.formatMessage(
                                        { id: "filtersSection.responses_tooltip_simple", defaultMessage: "{count} responses" },
                                        { count: value }
                                    );
                                }
                            }]}
                            width={900}
                            height={400}
                            margin={{ bottom: 60, top: 50, left: 90, right: 20 }}
                            slotProps={{
                                legend: { hidden: true }
                            }}
                            borderRadius={8}
                            sx={{
                                '& .MuiBarElement-root': {
                                    '&:hover': {
                                        opacity: 0.8
                                    }
                                },
                                '& .MuiChartsAxis-tickLabel': {
                                    whiteSpace: 'pre-line'
                                }
                            }}
                        />
                    </Box>
                )}
            </CardContent>

            {/* Attribute Distribution Detail Modal */}
            <Dialog
                open={attrDetailModalOpen}
                onClose={() => setAttrDetailModalOpen(false)}
                maxWidth="md"
                fullWidth
                disableScrollLock
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {selectedAttrDetail}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedAttrDetail && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                            {/* Distribution across Topics */}
                            {topicsData && Object.keys(topicsData).length > 0 && (
                                <Accordion sx={{ mb: 0, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            <FormattedMessage id="filtersSection.topics" defaultMessage="Topics" />
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {Object.entries(topicsData).map(([questionText, topics], qIdx) => {
                                            const relevantTopics = topics.filter(t =>
                                                (t.attr_topic_count?.[selectedAttrDetail] != null) ||
                                                (t.attr_diff?.[selectedAttrDetail] != null)
                                            );
                                            if (relevantTopics.length === 0) return null;

                                            return (
                                                <Box key={qIdx} sx={{ mb: qIdx < Object.keys(topicsData).length - 1 ? 2.5 : 0 }}>
                                                    <Box sx={{
                                                        px: 1.5, py: 0.75, bgcolor: '#e8f4fc',
                                                        borderRadius: 1, mb: 1.5, borderLeft: '3px solid #1976d2'
                                                    }}>
                                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1565c0' }}>
                                                            {questionText}
                                                        </Typography>
                                                    </Box>
                                                    {relevantTopics.map((topic, tIdx) => {
                                                        const count = topic.attr_topic_count?.[selectedAttrDetail] ?? 0;
                                                        const diff = topic.attr_diff?.[selectedAttrDetail] ?? 0;
                                                        const getSentimentColor = (d) => d > 0 ? '#4caf50' : d < 0 ? '#f44336' : '#ff9800';
                                                        return (
                                                            <Box key={tIdx} sx={{
                                                                mb: 1.5, ml: 1, borderRadius: 1, p: 1,
                                                                bgcolor: tIdx % 2 === 0 ? 'white' : '#fafafa',
                                                                border: '1px solid #eee',
                                                                fontSize: '0.8rem',
                                                            }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                            {topic.title}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {count}
                                                                            <Typography component="span" sx={{ fontSize: '0.55rem', color: 'text.disabled', ml: 0.5 }}>
                                                                                {' '}{intl.formatMessage({ id: "filtersSection.of_responses", defaultMessage: "of {total} responses" }, { total: topic.count ?? '—' })}
                                                                            </Typography>
                                                                        </Typography>
                                                                    </Box>
                                                                    <Chip
                                                                        label={`${(diff * 100).toFixed(2)}%`}
                                                                        size="small"
                                                                        sx={{ bgcolor: getSentimentColor(diff), color: 'white' }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{
                                                                    width: '100%', height: 8, bgcolor: '#e0e0e0',
                                                                    borderRadius: 1, overflow: 'hidden', position: 'relative'
                                                                }}>
                                                                    <Box sx={{
                                                                        position: 'absolute', left: '50%',
                                                                        width: `${Math.min(Math.abs(diff) * 50, 50)}%`,
                                                                        height: '100%', bgcolor: getSentimentColor(diff),
                                                                        transform: diff < 0 ? 'translateX(-100%)' : 'none',
                                                                        transition: 'width 0.3s ease'
                                                                    }} />
                                                                    <Box sx={{
                                                                        position: 'absolute', left: '50%', width: 2,
                                                                        height: '100%', bgcolor: '#000', transform: 'translateX(-50%)'
                                                                    }} />
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* Distribution across Likert Choices */}
                            {likertData?.likert_insights && Object.keys(likertData.likert_insights).length > 0 && (
                                <Accordion sx={{ mb: 0, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            <FormattedMessage id="filtersSection.likert_choices" defaultMessage="Likert Choices" />
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {Object.entries(likertData.likert_insights).map(([likertQText, likertChoices], lqIdx) => {
                                            if (!Array.isArray(likertChoices) || likertChoices.length === 0) return null;

                                            // For each likert choice, find this attribute's count and diff
                                            const choicesWithData = likertChoices.map((choice) => {
                                                let count = 0;
                                                let diff = 0;
                                                const atc = choice.attr_topic_count || {};
                                                const ad = choice.attr_diff || {};
                                                // Search all group questions for this attribute
                                                for (const [, attrs] of Object.entries(atc)) {
                                                    if (typeof attrs === 'object' && attrs !== null && attrs[selectedAttrDetail] != null) {
                                                        count = attrs[selectedAttrDetail];
                                                        break;
                                                    }
                                                }
                                                for (const [, attrs] of Object.entries(ad)) {
                                                    if (typeof attrs === 'object' && attrs !== null && attrs[selectedAttrDetail] != null) {
                                                        diff = attrs[selectedAttrDetail];
                                                        break;
                                                    }
                                                }
                                                return {
                                                    likertValue: choice.likert_value ?? '—',
                                                    totalCount: choice.count ?? 0,
                                                    count,
                                                    diff
                                                };
                                            });

                                            // Filter out choices with no relevant data
                                            const hasRelevantData = choicesWithData.some(c => c.count !== 0 || c.diff !== 0);
                                            if (!hasRelevantData) return null;

                                            return (
                                                <Box key={lqIdx} sx={{ mb: lqIdx < Object.keys(likertData.likert_insights).length - 1 ? 2.5 : 0 }}>
                                                    <Box sx={{
                                                        px: 1.5, py: 0.75, bgcolor: '#e8f4fc',
                                                        borderRadius: 1, mb: 1.5, borderLeft: '3px solid #1976d2'
                                                    }}>
                                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1565c0' }}>
                                                            {likertQText.trim()}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, ml: 1 }}>
                                                        {choicesWithData.map((cd, cIdx) => {
                                                            const getSentimentColor = (d) => d > 0 ? '#4caf50' : d < 0 ? '#f44336' : '#ff9800';
                                                            return (
                                                                <Paper
                                                                    key={cIdx}
                                                                    sx={{
                                                                        p: 1.5, minWidth: 120, textAlign: 'center',
                                                                        border: '1px solid #e0e0e0',
                                                                        bgcolor: cIdx % 2 === 0 ? 'white' : '#fafafa',
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                                        {cd.likertValue}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                                                                            <Typography variant="h6" color="primary.main" sx={{ lineHeight: 1.2 }}>{cd.count}</Typography>
                                                                            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>/ {cd.totalCount}</Typography>
                                                                        </Box>
                                                                        <Chip
                                                                            label={`${(cd.diff * 100).toFixed(2)}%`}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: getSentimentColor((cd.diff * 100).toFixed(2)),
                                                                                color: "white",
                                                                                mt: 0.5,
                                                                                height: 16,
                                                                                fontSize: 10,
                                                                                borderRadius: 1,
                                                                                "& .MuiChip-label": { px: 0.5, lineHeight: 1 },
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Paper>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {/* No data fallback */}
                            {(!topicsData || Object.keys(topicsData).length === 0) &&
                                (!likertData?.likert_insights || Object.keys(likertData.likert_insights).length === 0) && (
                                    <Typography variant="body2" color="text.secondary">
                                        <FormattedMessage id="filtersSection.no_distribution_data" defaultMessage="No distribution data available for this attribute." />
                                    </Typography>
                                )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAttrDetailModalOpen(false)}><FormattedMessage id="filtersSection.close" defaultMessage="Close" /></Button>
                </DialogActions>
            </Dialog>
        </Card >
    );
};

export default FiltersSection;